/**
 * TikTok Preview inline tool for EditorJS.
 * When user selects a TikTok URL, shows a "Preview" button in the inline toolbar.
 * Clicking it inserts a TikTok embed preview below the current block.
 */
export class TikTokPreviewTool {
  private _api: any;
  private _button: HTMLButtonElement | null = null;
  private _selectedUrl: string = '';
  private _isPreviewActive: boolean = false;

  static get isInline() {
    return true;
  }

  static get title() {
    return 'TikTok Preview';
  }

  constructor({ api }: any) {
    this._api = api;
  }

  render(): HTMLButtonElement {
    this._button = document.createElement('button');
    this._button.type = 'button';
    this._button.classList.add('ce-inline-tool');
    this._button.setAttribute('data-name', 'tiktokPreview');
    this._button.innerHTML = this._getToggleHtml(false);
    // Add pointer-events: none to all children via CSS
    const style = document.createElement('style');
    style.textContent = `
      .ce-inline-tool[data-name="tiktokPreview"] span {
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);
    return this._button;
  }

  private _getToggleHtml(isOn: boolean): string {
    const bgColor = isOn ? '#3b82f6' : '#e5e7eb';
    const transform = isOn ? 'translateX(16px)' : 'translateX(0)';
    return `
      <span style="display: inline-flex; align-items: center;">
        <span style="
          display: inline-block;
          width: 32px;
          height: 16px;
          background: ${bgColor};
          border-radius: 8px;
          position: relative;
          transition: background 0.2s;
        ">
          <span style="
            display: block;
            width: 12px;
            height: 12px;
            background: white;
            border-radius: 50%;
            position: absolute;
            top: 2px;
            left: 2px;
            transition: transform 0.2s;
            transform: ${transform};
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          "></span>
        </span>
      </span>
    `;
  }

  private _updateToggle(isOn: boolean, force: boolean = false): void {
    if (this._button && (force || this._isPreviewActive !== isOn)) {
      this._button.innerHTML = this._getToggleHtml(isOn);
      this._isPreviewActive = isOn;
    }
  }

  /**
   * Only show the button when a TikTok URL is selected.
   * Also saves the URL for use in surround() since selection is lost on click.
   * Checks if preview is already active for the current block.
   */
  checkState(): boolean {
    const selection = window.getSelection();
    const text = selection?.toString()?.trim() || '';
    const isTikTok = /tiktok\.com\/.+\/video\/\d+/.test(text);

    if (this._button) {
      this._button.style.display = isTikTok ? '' : 'none';
    }

    if (!isTikTok) {
      return false;
    }

    this._selectedUrl = text;
    const videoIdMatch = text.match(/video\/(\d+)/);
    const videoId = videoIdMatch ? videoIdMatch[1] : '';

    // Check if preview is already active for this URL by looking at next block
    const currentIndex = this._api.blocks.getCurrentBlockIndex();
    const nextBlock = this._api.blocks.getBlockByIndex(currentIndex + 1);

    // Get current block data to check if it has tiktokPreview state
    const currentBlock = this._api.blocks.getBlockByIndex(currentIndex);
    let isActive = false;

    if (currentBlock) {
      currentBlock.save().then((saved: any) => {
        // Check if this block has tiktokPreview state
        if (saved?.data?.tiktokPreview?.videoId === videoId && saved?.data?.tiktokPreview?.active) {
          isActive = true;
        }
        // Also check next block as fallback
        if (nextBlock) {
          nextBlock.save().then((nextSaved: any) => {
            const nextActive = nextSaved?.tool === 'tiktokEmbed' && nextSaved?.data?.videoId === videoId;
            this._updateToggle(isActive || nextActive, true);
          });
        } else {
          this._updateToggle(isActive, true);
        }
      });
    } else if (nextBlock) {
      nextBlock.save().then((saved: any) => {
        const nextActive = saved?.tool === 'tiktokEmbed' && saved?.data?.videoId === videoId;
        this._updateToggle(nextActive, true);
      });
    } else {
      this._updateToggle(false, true);
    }

    return false;
  }

  surround(): void {
    const url = this._selectedUrl;
    console.log('🎬 TikTok Preview surround called, url:', url);

    if (!url || !url.includes('tiktok.com')) return;

    // Extract video ID from URL
    const videoIdMatch = url.match(/video\/(\d+)/);
    if (!videoIdMatch) return;
    const videoId = videoIdMatch[1];

    // Get current block index
    const currentIndex = this._api.blocks.getCurrentBlockIndex();
    console.log('🎬 Current block index:', currentIndex);
    const currentBlock = this._api.blocks.getBlockByIndex(currentIndex);

    // Check if next block is already a TikTok embed (synchronous check)
    const nextBlock = this._api.blocks.getBlockByIndex(currentIndex + 1);
    let hasEmbed = false;
    if (nextBlock) {
      const blockData = nextBlock as any;
      // Try to get the block type directly from the block object
      const blockType = (blockData as any)?.holder?.firstElementChild?.classList?.contains('tiktok-embed-preview');
      hasEmbed = blockType || false;
    }

    console.log('🎬 Has embed next block:', hasEmbed);

    if (hasEmbed) {
      // Turn OFF: remove embed
      console.log('🎬 Turning OFF - removing embed');
      this._api.blocks.delete(currentIndex + 1);
      this._updateToggle(false);
      // Update block data
      if (currentBlock) {
        currentBlock.save().then((saved: any) => {
          this._api.blocks.update(saved.id, {
            ...saved.data,
            tiktokPreview: { videoId, active: false }
          });
        });
      }
    } else {
      // Turn ON: insert embed
      console.log('🎬 Turning ON - inserting embed');
      this._insertEmbed(currentIndex, videoId, url);
      this._updateToggle(true);
      // Update block data
      if (currentBlock) {
        currentBlock.save().then((saved: any) => {
          this._api.blocks.update(saved.id, {
            ...saved.data,
            tiktokPreview: { videoId, active: true }
          });
        });
      }
    }
  }

  private _insertEmbed(afterIndex: number, videoId: string, url: string): void {
    this._api.blocks.insert('tiktokEmbed', { videoId, url }, {}, afterIndex + 1);
  }
}
